import * as React from "react";
import {cx} from "emotion";

import {ReactGACartEvent}     from "@Common/libs/ga/xd-react-ga";
import {XdSentry}             from "@Common/libs/sentry/xd-sentry";
import {CheckoutSentryLogger} from "@Common/logic/checkout/checkout-sentry-logger";
import {cs}                   from "@Common/react/chain-services";
import {consumeContext}       from "@Common/react/context";
import {UseState}             from "@Common/react/use-state";
import {buildUrlQuery}        from "@Common/utils/build-url-query";

import {ApproveConfirmModal} from "../../../../approval-requests/common/approve-confirm-modal/approve-confirm-modal";

export const Actions = (
    {
        cart,
        query,
        showErrors,
        sections,
        approval,
        loading,
        cartProcessingModal,
    }
) => cs(
    ["cart", (_, next) => next(cart)],
    consumeContext("apis"),
    consumeContext("userLevels"),
    consumeContext("routerHistory"),
    ["submitting", (_, next) => UseState({next})],
    ["rootCart", (_, next) => cs(
        consumeContext("cart"),
        ({cart}) => next(cart),
    )],
    ["valid", ({submitting}, next) => next(
        !submitting.value &&
        !loading &&
        !sections.invalid &&
        !cart.value.reproofRequired &&
        true
    )],
    ["logger", (_, next) => next(CheckoutSentryLogger({Sentry: XdSentry}))],

    ["approveConfirmModal", ({}, next) => ApproveConfirmModal({next})],
    ["action", ({cart, submitting, apis, logger, rootCart, userLevels, approveConfirmModal, routerHistory}, next) => next(

        query?.referrer === "approval-requests" ? {
            label: "Approve",
            onClick: async () => approveConfirmModal.show({
                id: cart.value.cartId,
                finalApprovalLevel: cart.finalApprovalLevel,
                confirm: async () => {
                    submitting.onChange(true);
                    await sections.beforeCheckout();
                    const result = await apis.next.approval.approveCcCart(cart.value.cartId);

                    if (result.result === "failure") {
                        logger.logCheckoutApprovalFailure({cartId: cart.value.cartId, result});
                        submitting.onChange(false);
                        cart.reload();
                        return {success: false, error: result.errors.msg};
                    } else {
                        logger.logCheckoutApprovalSuccess({cartId: cart.value.cartId, result});
                        if (cart.value.cartId === rootCart.value?.cartId) {
                            rootCart.onChange(null);
                        }
                        if (result?.submitToNextApprover) {
                            alert(`This order has been approved and sent to the ${userLevels.userLevel == 4 ? "company" : "office" } admin for final approval.`);
                            routerHistory.push(`/account/orders/approval`);
                            return {success: true,};
                        } else {
                            routerHistory.push(`/checkout-success/${cart.value.cartId}` + buildUrlQuery({
                                referrer: query?.referrer,
                            }));
                            return {success: true,};

                        }
                    }
                },
            }),
            navigate: {
                label: "Save & Return to Dashboard",
                to: "/account/orders/approval",
                requireValidation: true,
            },
        } : {
            navigate: {
                label: "Continue Shopping",
                to: "/"
            },
            ...approval.loading ? {
                label: "...",
            } : (() => {
                return approval.active ? {
                    label: approval.actionLabel,
                    onClick: async () => {
                        submitting.onChange(true);

                        await sections.beforeCheckout();
                        try {
                            await approval.submit({logger});
                        } finally {
                            submitting.onChange(false);
                        }
                    }
                } : cart.value.cartType.toUpperCase() === 'AUTO' ? {
                    label: "Save And Return",
                    onClick: async () => {
                        routerHistory.push(`/account/program-defaults/scheduled-campaigns`);
                    }
                } : {
                    label: "Checkout",
                    onClick: async () => {
                        submitting.onChange(true);
                        cartProcessingModal.show();
                        await sections.beforeCheckout();

                        const result = await checkoutCart(cart.value, {apis});

                        if (result.result === "failure") {
                            logger.logCheckoutFailure({cartId: cart.value.cartId, result});
                            // {"result":"failure","errors":{"target":"checkout","msg":"Failed while processing cart"}}
                            if(result.errors.msg){
                                alert(result.errors.msg);
                            } else if(result.errors[0]?.msg){
                                alert(result.errors[0].msg);
                            }
                            submitting.onChange(false);
                            cart.reload();
                            cartProcessingModal.hide();
                        } else {
                            logger.logCheckoutSuccess({cartId: cart.value.cartId, result});
                            ReactGACartEvent({eventName:"purchase", cart: cart.value});
                            if (cart.value.cartId === rootCart.value?.cartId) {
                                rootCart.onChange(null);
                            }
                        }
                    }
                }
            })(),
        }
    )],


    ({action, valid, submitting, cart, userLevels, routerHistory}) => (
        <>
            {!loading && showErrors.value && userLevels?.isGuest ? (<div className="error">
                    {"Something went wrong. Please refresh the page."}
                </div>
            ) : (
                !loading && showErrors.value && sections.errorMessages.map((msg, i) => (
                    <div className="error" key={i}>
                        {msg}
                    </div>
                ))
            )}

            {cart.value.reproofRequired && (
                <div className="error">
                    Proof files are missing for orders
                    <span className="reproofOrders">
                        {" " + cart.value.reproofRequiredOrders.join(" , ")}
                    </span>. Please go to customization page and regenerate the proof.
                </div>
            )}

            <button {...{
                className: cx({disabled: !valid || userLevels?.isGuest}),
                onClick: async () => {
                    if (!valid || userLevels?.isGuest) {
                        showErrors.onChange(true);
                        sections.highlightErrors?.();
                    } else {
                        action.onClick();
                    }
                },
                "xd-qa-type": "button",
                "xd-qa-name": action.label,
            }}>
                {action.label}
            </button>

            {action?.navigate && (
                <a
                    className={cx({disabled: action.navigate.requireValidation && !valid})}
                    onClick={async () => {
                        if (action.navigate.requireValidation && !valid) {
                            showErrors.onChange(true);
                            sections.highlightErrors?.();
                            return;
                        } else if (submitting.value) {
                            return;
                        }
                        submitting.onChange(true);
                        await sections.beforeCheckout();

                        routerHistory.push(action.navigate.to);
                    }}
                    xd-qa-type= "link"
                    xd-qa-name= {action.navigate.label}
                >
                    {action.navigate.label}
                </a>
            ) }
        </>
    ),
);

const checkoutCart = async (cart, {apis}) => {
    const cartId = cart.cartId;
    return await apis.next.cart.checkoutCart(cartId);
};
